import { FC, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import styled from 'styled-components';
import { useScrollWithBoundary } from '~/common/hooks/useScrollWithBoundary';
import { Image } from '~/components/wrappers/Image';
import { ProductMedia } from '~/types/product';
import CarouselArrow from 'public/assets/icons/carousel_arrow_left.svg';
import PlayIcon from 'public/assets/icons/play.svg';

interface ThumbnailGalleryProps {
  media: ProductMedia[];
  productTitle: string;
  currentIndex: number;
  onThumbnailClick: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  thumbnailScrollTopRef?: React.MutableRefObject<number>;
}

export const ThumbnailGallery: FC<ThumbnailGalleryProps> = ({
  media,
  productTitle,
  currentIndex,
  onThumbnailClick,
  onPrev,
  onNext,
  thumbnailScrollTopRef,
}) => {
  const thumbnailListRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const didDragRef = useRef(false);

  useScrollWithBoundary({
    scrollRef: thumbnailListRef,
    containerRef: thumbnailListRef,
  });

  useLayoutEffect(() => {
    const list = thumbnailListRef.current;
    const ref = thumbnailScrollTopRef;
    if (!list || !ref) return;
    list.scrollTop = ref.current;
    const onScroll = () => {
      ref.current = list.scrollTop;
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    return () => list.removeEventListener('scroll', onScroll);
  }, [thumbnailScrollTopRef]);

  useEffect(() => {
    const list = thumbnailListRef.current;
    if (!list) return;

    let startY = 0;
    let startScrollTop = 0;
    let dragging = false;

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      list.style.cursor = '';
      list.style.userSelect = '';
      requestAnimationFrame(() => {
        didDragRef.current = false;
      });
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragging = true;
      didDragRef.current = false;
      startY = e.clientY;
      startScrollTop = list.scrollTop;
      list.style.cursor = 'grabbing';
      list.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const delta = startY - e.clientY;
      if (Math.abs(delta) > 3) didDragRef.current = true;
      list.scrollTop = Math.max(
        0,
        Math.min(list.scrollHeight - list.clientHeight, startScrollTop + delta),
      );
    };

    list.addEventListener('mousedown', onMouseDown);
    list.addEventListener('mouseleave', endDrag);
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', endDrag, true);

    return () => {
      list.removeEventListener('mousedown', onMouseDown);
      list.removeEventListener('mouseleave', endDrag);
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', endDrag, true);
    };
  }, []);

  const scrollGalleryToIndex = useCallback((index: number) => {
    const list = thumbnailListRef.current;
    const thumbnail = thumbnailRefs.current[index];
    if (!list || !thumbnail) return;

    const listRect = list.getBoundingClientRect();
    const thumbRect = thumbnail.getBoundingClientRect();
    const thumbTopInList = list.scrollTop + (thumbRect.top - listRect.top);
    const thumbHeight = thumbRect.height;
    const listHeight = list.clientHeight;
    const maxScroll = list.scrollHeight - listHeight;

    const targetScrollTop = Math.max(
      0,
      Math.min(maxScroll, thumbTopInList - listHeight / 2 + thumbHeight / 2),
    );

    list.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }, []);

  const handlePrev = useCallback(() => {
    const prevIndex = currentIndex === 0 ? media.length - 1 : currentIndex - 1;
    onPrev();
    scrollGalleryToIndex(prevIndex);
  }, [currentIndex, media.length, onPrev, scrollGalleryToIndex]);

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex === media.length - 1 ? 0 : currentIndex + 1;
    onNext();
    scrollGalleryToIndex(nextIndex);
  }, [currentIndex, media.length, onNext, scrollGalleryToIndex]);

  return (
    <ThumbnailColumn>
      <NavArrowButton onClick={handlePrev} aria-label="Previous image">
        <ArrowIcon
          $rotation={90}
          src={CarouselArrow}
          alt="Navigate to previous product image"
          loading="lazy"
        />
      </NavArrowButton>
      <ThumbnailListWrapper>
        <ThumbnailList ref={thumbnailListRef}>
          {media.map((item, index) => {
            const previewSrc =
              item.kind === 'image' ? item.src : item.previewImage;

            return (
              <Thumbnail
                key={item.id}
                ref={(el) => {
                  thumbnailRefs.current[index] = el;
                }}
                onClick={() => {
                  if (!didDragRef.current) onThumbnailClick(index);
                }}
              >
                {previewSrc && (
                  <ThumbnailImage
                    src={`${previewSrc}&width=100`}
                    alt={`${productTitle} - thumbnail ${index + 1}`}
                    loading="lazy"
                    draggable={false}
                  />
                )}
                {item.kind === 'video' && (
                  <PlayBadge>
                    <PlayBadgeIcon
                      src={PlayIcon}
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                  </PlayBadge>
                )}
              </Thumbnail>
            );
          })}
        </ThumbnailList>
      </ThumbnailListWrapper>
      <NavArrowButtonBottom onClick={handleNext} aria-label="Next image">
        <ArrowIcon
          $rotation={-90}
          src={CarouselArrow}
          alt="Navigate to next product image"
          loading="lazy"
        />
      </NavArrowButtonBottom>
    </ThumbnailColumn>
  );
};

const ThumbnailColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4.25rem 2.5rem 4.25rem 1.5rem;
  flex-shrink: 0;
  max-height: 100vh;
  position: sticky;
  top: 0;
  align-self: flex-start;

  @media ${({ theme }) => theme.breakpoints.md} {
    display: none;
  }
`;

const NavArrowButton = styled.button`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const NavArrowButtonBottom = styled(NavArrowButton)``;

const ArrowIcon = styled(Image)<{ $rotation?: number }>`
  width: 1.25rem;
  height: 1.25rem;
  transform: ${({ $rotation }) =>
    $rotation ? `rotate(${$rotation}deg)` : 'none'};
`;

const ThumbnailListWrapper = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ThumbnailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Thumbnail = styled.button`
  width: 4.6875rem;
  height: 6.25rem;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
`;

const ThumbnailImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PlayBadgeIcon = styled(Image)`
  width: 0.75rem;
  height: 0.75rem;
  filter: invert(1);
`;
